declare const ERROR_CODE_BRAND: unique symbol

export type ErrorCode = {
  status: number
  type: string
  message: string
  readonly [ERROR_CODE_BRAND]: true
}

type RawErrorCode = Omit<ErrorCode, typeof ERROR_CODE_BRAND>

const defineError = (error: RawErrorCode): ErrorCode => error as ErrorCode

export const BAD_REQUEST: ErrorCode = defineError({
  status: 400,
  type: 'BAD_REQUEST',
  message: 'Bad Request'
})

export const UNAUTHORIZED: ErrorCode = defineError({
  status: 401,
  type: 'UNAUTHORIZED',
  message: 'Unauthorized'
})

export const NOT_FOUND: ErrorCode = defineError({
  status: 404,
  type: 'NOT_FOUND',
  message: 'Not Found'
})

export const REQUEST_TIMEOUT: ErrorCode = defineError({
  status: 408,
  type: 'REQUEST_TIMEOUT',
  message: 'Request Timeout'
})

export const INTERNAL_SERVER_ERROR: ErrorCode = defineError({
  status: 500,
  type: 'INTERNAL_SERVER_ERROR',
  message: 'Internal Server Error'
})

export const PARAMETER_INVALID: ErrorCode = defineError({
  status: 400,
  type: 'PARAMETER_INVALID',
  message: 'The parameter is invalid.'
})

export const REQUIRED_PARAM_NOT_EXIST: ErrorCode = defineError({
  status: 400,
  type: 'REQUIRED_PARAM_NOT_EXIST',
  message: 'Required parameter does not exist.'
})

export const NO_DATA_EXISTS: ErrorCode = defineError({
  status: 400,
  type: 'NO_DATA_EXISTS',
  message: 'No data exists.'
})

export const NOT_EXISTS: ErrorCode = defineError({
  status: 400,
  type: 'NOT_EXISTS',
  message: 'Not exists.'
})

export const PERMISSION_DENIED: ErrorCode = defineError({
  status: 400,
  type: 'PERMISSION_DENIED',
  message: 'Permission denied.'
})

/**
 * データが重複している場合のエラー
 */
export const DUPLICATE: ErrorCode = defineError({
  status: 400,
  type: 'DUPLICATE',
  message: 'Duplicate.'
})

export const MAX_CAMPAIGN_COUNT_OVER: ErrorCode = defineError({
  status: 400,
  type: 'MAX_CAMPAIGN_COUNT_OVER',
  message: 'Max campaign count is over.'
})

export const MAX_WINNER_COUNT_OVER: ErrorCode = defineError({
  status: 400,
  type: 'MAX_WINNER_COUNT_OVER',
  message: 'Max winner count is over.'
})

export const MAX_TWEET_POST_URL_COUNT_OVER: ErrorCode = defineError({
  status: 400,
  type: 'MAX_TWEET_POST_URL_COUNT_OVER',
  message: 'Max tweet post url count is over.'
})

export const REMAIN_WINNER_OVER: ErrorCode = defineError({
  status: 400,
  type: 'REMAIN_WINNER_OVER',
  message: 'Remain winner is over.'
})

export const MAX_COUNT_OVER: ErrorCode = defineError({
  status: 400,
  type: 'MAX_COUNT_OVER',
  message: 'Max count is over.'
})

export const CURRENT_COUNT_OVER: ErrorCode = defineError({
  status: 400,
  type: 'CURRENT_COUNT_OVER',
  message: 'Current count is over.'
})

export const CURRENT_CAMPAIGN_COUNT_OVER: ErrorCode = defineError({
  status: 400,
  type: 'CURRENT_CAMPAIGN_COUNT_OVER',
  message: 'Current campaign count is over.'
})

export const CURRENT_MONTHLY_APPLICANT_COUNT_OVER: ErrorCode = defineError({
  status: 400,
  type: 'CURRENT_MONTHLY_APPLICANT_COUNT_OVER',
  message: 'Current monthly applicant count is over.'
})

export const CURRENT_MONTHLY_DM_COUNT_OVER: ErrorCode = defineError({
  status: 400,
  type: 'CURRENT_MONTHLY_DM_COUNT_OVER',
  message: 'Current monthly DM count is over.'
})

export const MESSAGE_QUEUE_COUNT_OVER: ErrorCode = defineError({
  status: 400,
  type: 'MESSAGE_QUEUE_COUNT_OVER',
  message: 'Message queue count is over.'
})

export const CAMPAIGN_SCHEDULED: ErrorCode = defineError({
  status: 400,
  type: 'CAMPAIGN_SCHEDULED',
  message: 'Campaign is scheduled.'
})

export const CAMPAIGN_START_DATETIME_INVALID: ErrorCode = defineError({
  status: 400,
  type: 'CAMPAIGN_START_DATETIME_INVALID',
  message: 'Campaign start datetime is invalid.'
})

export const CAMPAIGN_STATUS_INVALID: ErrorCode = defineError({
  status: 400,
  type: 'CAMPAIGN_STATUS_INVALID',
  message: 'Campaign status is invalid.'
})

export const MAX_LIMIT_OVER: ErrorCode = defineError({
  status: 400,
  type: 'MAX_LIMIT_OVER',
  message: 'Max limit is over.'
})

/**
 * QRコードが生成できない場合のエラー
 */
export const QRCODE_GENERATE_FAILED: ErrorCode = defineError({
  status: 400,
  type: 'QRCODE_GENERATE_FAILED',
  message: 'Failed to generate qrcode.'
})

export const FACEBOOK_PAGE_NOT_FOUND: ErrorCode = defineError({
  status: 400,
  type: 'FACEBOOK_PAGE_NOT_FOUND',
  message: 'Facebook page not found.'
})

export const DM_SENDING_IN_PROGRESS: ErrorCode = defineError({
  status: 400,
  type: 'DM_SENDING_IN_PROGRESS',
  message: 'DM sending in progress.'
})

export const INSTAGRAM_MEDIA_UPLOAD_FAILED: ErrorCode = defineError({
  status: 400,
  type: 'INSTAGRAM_MEDIA_UPLOAD_FAILED',
  message: 'Instagram media upload failed.'
})

/**
 * ファイルアップロード関連エラー
 */
export const INVALID_FILE_TYPE: ErrorCode = defineError({
  status: 400,
  type: 'INVALID_FILE_TYPE',
  message: 'Invalid file type.'
})

export const FILE_TOO_LARGE: ErrorCode = defineError({
  status: 400,
  type: 'FILE_TOO_LARGE',
  message: 'File too large.'
})

export const UPLOAD_FAILED: ErrorCode = defineError({
  status: 500,
  type: 'UPLOAD_FAILED',
  message: 'upload failed.'
})

/**
 * Instagram Instant Win ノードコンテナ関連エラー
 */
export const CONTAINER_ID_REQUIRED: ErrorCode = defineError({
  status: 400,
  type: 'CONTAINER_ID_REQUIRED',
  message: 'Container ID is required. Container creation is not supported.'
})

export const NODE_ID_REQUIRED: ErrorCode = defineError({
  status: 400,
  type: 'NODE_ID_REQUIRED',
  message: 'Node ID is required for update.'
})

export const NODE_NOT_FOUND: ErrorCode = defineError({
  status: 400,
  type: 'NODE_NOT_FOUND',
  message: 'Node not found.'
})

export const PARENT_NODE_NOT_FOUND: ErrorCode = defineError({
  status: 400,
  type: 'PARENT_NODE_NOT_FOUND',
  message: 'Parent node not found.'
})

export const NODE_PARENT_REQUIRED: ErrorCode = defineError({
  status: 400,
  type: 'NODE_PARENT_REQUIRED',
  message:
    'Cannot add node without parent to a container that already has nodes. New nodes must specify parentId or parentTempId.'
})

export const NOT_MATCH: ErrorCode = defineError({
  status: 400,
  type: 'NOT_MATCH',
  message: 'Not match.'
})

export const GROUP_USER_EXISTS: ErrorCode = defineError({
  status: 400,
  type: 'GROUP_USER_EXISTS',
  message: 'Group user exists.'
})

export const GROUP_MAX_USER_COUNT_OVER: ErrorCode = defineError({
  status: 400,
  type: 'GROUP_MAX_USER_COUNT_OVER',
  message: 'Group max user count is over.'
})

export const INVITATION_NOT_EXISTS: ErrorCode = defineError({
  status: 400,
  type: 'INVITATION_NOT_EXISTS',
  message: 'Invitation does not exist.'
})

export const PARAMETER_NOT_PRESENT: ErrorCode = defineError({
  status: 400,
  type: 'PARAMETER_NOT_PRESENT',
  message: 'Parameter not present.'
})

export const USER_NOT_EXISTS: ErrorCode = defineError({
  status: 400,
  type: 'USER_NOT_EXISTS',
  message: 'User does not exist.'
})

export const AUTH_FAIL: ErrorCode = defineError({
  status: 400,
  type: 'AUTH_FAIL',
  message: 'Authentication failed.'
})

export const LOGIN_ATTEMPTS_EXCEEDED: ErrorCode = defineError({
  status: 400,
  type: 'LOGIN_ATTEMPTS_EXCEEDED',
  message: 'Login attempts exceeded.'
})

export const NOT_CONNECTED: ErrorCode = defineError({
  status: 400,
  type: 'NOT_CONNECTED',
  message: 'Not connected.'
})

export const CODE_EXPIRED: ErrorCode = defineError({
  status: 400,
  type: 'CODE_EXPIRED',
  message: 'Code expired.'
})

export const ERROR: ErrorCode = defineError({
  status: 400,
  type: 'ERROR',
  message: 'Error.'
})
